import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollText, Download } from 'lucide-react';

export const AuditTab = ({
  auditLogs, auditActions, auditFilter, setAuditFilter, auditTotal, auditLoading,
  fetchAuditLogs, csvRange, setCsvRange, exportingCsv, exportAuditCsv,
}) => (
  <div className="space-y-6" data-testid="audit-tab">
    <Card className="card-tech p-6">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="text-xl font-bold text-green-400">
            <ScrollText className="w-5 h-5 inline mr-2" /> System Audit Trail
          </h3>
          <p className="text-green-500/70 text-sm">
            Every sensitive admin action is logged here — logins, config changes, password rotations, staff payouts, tax exports.
            Secrets are redacted. Showing latest 100 · Total records: <span className="text-green-400 font-bold">{auditTotal}</span>
          </p>
        </div>
        <Button onClick={fetchAuditLogs} disabled={auditLoading} className="btn-secondary text-xs" data-testid="audit-refresh-btn">
          {auditLoading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block text-green-400 text-xs font-semibold mb-1">Action</label>
          <select value={auditFilter.action} onChange={e => setAuditFilter({ ...auditFilter, action: e.target.value })}
            className="w-full bg-black/50 border border-green-500/30 text-green-300 rounded-md h-10 px-2 text-sm" data-testid="audit-filter-action">
            <option value="">All actions</option>
            {auditActions.map(a => (<option key={a} value={a}>{a}</option>))}
          </select>
        </div>
        <div>
          <label className="block text-green-400 text-xs font-semibold mb-1">Actor</label>
          <Input value={auditFilter.actor} onChange={e => setAuditFilter({ ...auditFilter, actor: e.target.value })}
            placeholder="username" className="bg-black/50 border-green-500/30 text-green-300 h-10 text-sm" data-testid="audit-filter-actor" />
        </div>
        <div>
          <label className="block text-green-400 text-xs font-semibold mb-1">Status</label>
          <select value={auditFilter.status} onChange={e => setAuditFilter({ ...auditFilter, status: e.target.value })}
            className="w-full bg-black/50 border border-green-500/30 text-green-300 rounded-md h-10 px-2 text-sm" data-testid="audit-filter-status">
            <option value="">Any status</option>
            <option value="success">success</option>
            <option value="failure">failure</option>
          </select>
        </div>
      </div>

      {/* CSV Export controls */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 p-3 border border-green-500/10 rounded bg-green-500/5" data-testid="audit-csv-controls">
        <div>
          <label className="block text-green-400 text-xs font-semibold mb-1">Export from (date)</label>
          <Input type="date" value={csvRange.start_date} onChange={e => setCsvRange({ ...csvRange, start_date: e.target.value })}
            className="bg-black/50 border-green-500/30 text-green-300 h-10 text-sm" data-testid="audit-csv-start" />
        </div>
        <div>
          <label className="block text-green-400 text-xs font-semibold mb-1">Export to (date)</label>
          <Input type="date" value={csvRange.end_date} onChange={e => setCsvRange({ ...csvRange, end_date: e.target.value })}
            className="bg-black/50 border-green-500/30 text-green-300 h-10 text-sm" data-testid="audit-csv-end" />
        </div>
        <div className="flex items-end">
          <Button onClick={exportAuditCsv} disabled={exportingCsv} className="btn-primary w-full" data-testid="audit-csv-export-btn">
            <Download className="w-4 h-4 mr-2" />
            {exportingCsv ? 'Exporting…' : 'Export CSV'}
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-green-500/20">
              <th className="text-left text-green-400 py-2 px-2 text-xs">Time (UTC)</th>
              <th className="text-left text-green-400 py-2 px-2 text-xs">Action</th>
              <th className="text-left text-green-400 py-2 px-2 text-xs">Actor</th>
              <th className="text-left text-green-400 py-2 px-2 text-xs">Status</th>
              <th className="text-left text-green-400 py-2 px-2 text-xs">IP</th>
              <th className="text-left text-green-400 py-2 px-2 text-xs">Details</th>
            </tr>
          </thead>
          <tbody>
            {auditLogs.map((log, idx) => (
              <tr key={`${log.timestamp}-${idx}`} className="border-b border-green-500/10" data-testid={`audit-row-${idx}`}>
                <td className="py-2 px-2 text-green-300 text-xs font-mono">{(log.timestamp || '').slice(0, 19).replace('T', ' ')}</td>
                <td className="py-2 px-2 text-green-300 text-xs font-mono">{log.action}</td>
                <td className="py-2 px-2 text-green-300 text-xs">{log.actor}</td>
                <td className="py-2 px-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${log.status === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {log.status}
                  </span>
                </td>
                <td className="py-2 px-2 text-green-300 text-xs font-mono">{log.ip}</td>
                <td className="py-2 px-2 text-green-300 text-xs max-w-md truncate" title={JSON.stringify(log.details)}>
                  {Object.keys(log.details || {}).length > 0
                    ? Object.entries(log.details).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ')
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {auditLogs.length === 0 && !auditLoading && (
          <p className="text-center text-green-500/50 py-8">No audit events match the current filters.</p>
        )}
      </div>
    </Card>
  </div>
);
